import { useState } from "react";
import { Download, Loader2, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Section, Param } from "@/components/editor/Section";
import { MasterMeter } from "@/components/editor/MasterMeter";
import { renderMix, encodeWav } from "@/lib/audioEngine";
import { downloadBlob } from "@/lib/grade";

const MAX_DECODE = 500 * 1024 * 1024;

export const SoundPanel = ({ engine, mixer, setMixer, clips, assetsMap, duration, projectName, openConsole }) => {
  const [busy, setBusy] = useState(false);

  const exportMix = async () => {
    setBusy(true);
    try {
      const buffers = new Map();
      let skipped = 0;
      for (const c of clips) {
        const a = assetsMap[c.asset_id];
        if (!a || a.status !== "ready" || c.track === "fx") continue;
        if (c.track === "video" && a.size > MAX_DECODE) { skipped++; continue; }
        try { buffers.set(c.asset_id, await engine.load(c.asset_id)); } catch (_) { skipped++; }
      }
      if (!buffers.size) throw new Error("Add audio clips to the timeline first");
      const out = await renderMix(clips, buffers, mixer, duration);
      downloadBlob(encodeWav(out), `${(projectName || "mix").replace(/[^\w-]+/g, "_")}_mix.wav`);
      toast.success(skipped ? `Mix exported (${skipped} clip(s) skipped — picture audio over 500 MB is mixed in your NLE)` : "Mix exported as 44.1 kHz WAV");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div data-testid="sound-panel">
      <Section title="Master bus">
        <div className="flex gap-4 items-center">
          <MasterMeter engine={engine} height={90} />
          <div className="flex-1"><Param label="Master" value={mixer.master} min={-60} max={12} step={0.5} unit=" dB" onChange={(v) => setMixer({ ...mixer, master: v })} testId="sound-master-gain" /></div>
        </div>
        <Button variant="outline" onClick={openConsole} className="w-full mt-3 border-[#2A2E3D] bg-[#0B0C10]" data-testid="open-console-button"><SlidersHorizontal className="w-4 h-4 mr-2" />Open mixing console</Button>
      </Section>
      <Section title="Bounce">
        <p className="text-xs text-slate-400 mb-3">Renders every audio clip through its bus EQ, compression, reverb, pan & faders into a stereo 44.1 kHz / 16-bit WAV.</p>
        <Button onClick={exportMix} disabled={busy} className="w-full bg-[#0D9488] hover:bg-[#14b8a6] text-white" data-testid="export-mix-button">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}Export mixed WAV
        </Button>
      </Section>
      <Section title="Mixing tips">
        <ul className="text-[11px] text-slate-400 space-y-1.5 list-disc pl-4">
          <li>Keep dialogue peaking around −6 dB; tuck score 8–12 dB under it.</li>
          <li>Cut LOW on SFX bus to keep footsteps clean; add 10–20% verb to sit foley in the room.</li>
          <li>Compress dialogue at 3:1, threshold −24 dB for broadcast-style consistency.</li>
        </ul>
      </Section>
    </div>
  );
};
